import { Pill } from '@innovationdepartment/proxima-ui';
import { DataObjectOutlined, ErrorOutline, SubjectOutlined } from '@mui/icons-material';
import type { ExecutionDetails, ExecutionTask } from '../BrandWorkflows.types';
import {
  formatWorkflowDuration,
  formatWorkflowTimestamp,
  httpsUrl,
  s3ConsoleUrl,
  taskPillVariant
} from '../workflowFormatters';
import {
  ArtifactChip,
  ArtifactsCell,
  FailedBlock,
  FailedText,
  TaskKey,
  TaskRow,
  TasksHeaderRow,
  TasksSection,
  Wrapper
} from './WorkflowTasksDetails.styles';

const taskHasArtifacts = (task: ExecutionTask) =>
  Boolean(task.payloadUri || task.resultUri || httpsUrl(task.cloudWatchUrl));

type WorkflowTasksDetailsViewProps = {
  execution: ExecutionDetails;
};

const WorkflowTasksDetailsView = ({ execution }: WorkflowTasksDetailsViewProps) => (
  <Wrapper>
    {execution.status === 'Failure' && (
      <FailedBlock>
        <ErrorOutline />
        <FailedText>
          <span>
            Failed task: <code>{execution.failedTaskKey ?? 'unknown'}</code>
          </span>
          {execution.errorMessage ? <span>{execution.errorMessage}</span> : null}
        </FailedText>
      </FailedBlock>
    )}

    <TasksSection>
      <TasksHeaderRow>
        <span>Task_ID</span>
        <span>Task</span>
        <span>Status</span>
        <span>Duration</span>
        {/* spacer: flexes the started/ended/artifacts group to the right edge */}
        <span />
        <span>Started</span>
        <span>Ended</span>
        <span>Artifacts</span>
      </TasksHeaderRow>
      {execution.tasks.map(task => {
        const cloudWatchUrl = httpsUrl(task.cloudWatchUrl);
        return (
          <TaskRow key={task.taskId}>
            <span>{task.taskId}</span>
            <TaskKey>{task.taskKey}</TaskKey>
            <span>
              <Pill label={task.status} variant={taskPillVariant(task.status)} />
            </span>
            <span>{formatWorkflowDuration(task.startedAt, task.endedAt)}</span>
            {/* spacer: flexes the started/ended/artifacts group to the right edge */}
            <span />
            <span>{formatWorkflowTimestamp(task.startedAt)}</span>
            <span>{formatWorkflowTimestamp(task.endedAt)}</span>
            <ArtifactsCell>
              {taskHasArtifacts(task) ? (
                <>
                  {task.payloadUri ? (
                    <ArtifactChip
                      href={s3ConsoleUrl(task.payloadUri) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DataObjectOutlined /> payload.json
                    </ArtifactChip>
                  ) : null}
                  {task.resultUri ? (
                    <ArtifactChip
                      href={s3ConsoleUrl(task.resultUri) ?? undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <DataObjectOutlined /> output.json
                    </ArtifactChip>
                  ) : (
                    <ArtifactChip as="span" disabled>
                      <DataObjectOutlined /> output.json
                    </ArtifactChip>
                  )}
                  {cloudWatchUrl && (
                    <ArtifactChip href={cloudWatchUrl} target="_blank" rel="noreferrer">
                      <SubjectOutlined /> logs
                    </ArtifactChip>
                  )}
                </>
              ) : (
                '—'
              )}
            </ArtifactsCell>
          </TaskRow>
        );
      })}
    </TasksSection>
  </Wrapper>
);

export default WorkflowTasksDetailsView;
